/**
 * @file AuthContext.js
 * @brief Global authentication state management
 * 
 * @details
 * React Context for managing user authentication across the app.
 * Automatically persists tokens to localStorage.
 * Provides useAuth hook for accessing auth state in components.
 * 
 * @features
 * - Global auth state (user, token, loading, error)
 * - Automatic token persistence (localStorage)
 * - Auto token verification on app load
 * - Protected route support via PrivateRoute component
 * - Logout with cleanup
 * 
 * @usage
 * // Wrap app with provider (in App.js):
 * <AuthProvider>
 *   <AppContent />
 * </AuthProvider>
 * 
 * // Use in components:
 * const { user, token, login, logout, isAuthenticated } = useAuth();
 * 
 * @methods
 * - register(username, email, password, firstName, lastName) - Create new account
 * - login(email, password) - Login to existing account
 * - logout() - Clear auth state and token
 * 
 * @properties
 * - user: Current logged-in user object or null
 * - token: JWT token string or null
 * - loading: Boolean indicating async operation in progress
 * - error: Error message string or null
 * - isAuthenticated: Boolean (true if user && token exist)
 * 
 * @dependencies react, axios
 * @author FantasticTennis Team
 * @version 1.0.0
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // Configure axios to include token in requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
      
      // Verify token and fetch user
      verifyToken();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  // Verify token and get user data
  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data.user);
      setError(null);
    } catch (err) {
      console.error('Token verification failed:', err);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Register user
  const register = async (username, email, password, firstName, lastName) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${API_URL}/auth/register`, {
        username,
        email,
        password,
        firstName,
        lastName,
      });

      setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      setToken(response.data.token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    setToken(null);
    setUser(null);
    setError(null);
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  const value = {
    user,
    token,
    loading,
    error,
    register,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
